import React, { useState } from 'react';
import { spotsApi } from '../../api/client';
import { Spot } from '../../types';
import { FeedCard } from '../../components/FeedCard/FeedCard';
import { useCarMakes } from '../../hooks/useCarMakes';
import styles from './SearchSpots.module.css';

export function SearchSpots() {
  const [make, setMake] = useState('');
  const [model, setModel] = useState('');
  const [results, setResults] = useState<Spot[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const { carMakes, loading: carMakesLoading, error: carMakesError } = useCarMakes();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!make.trim() && !model.trim()) {
      setError('Enter a make or model to search.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const apiResults = await spotsApi.search(make, model);
      const spots: Spot[] = apiResults.map((s) => ({
        id: s.id,
        authorId: s.authorId,
        author: s.author,
        imageUrl: s.imageUrl,
        images: Array.isArray(s.images) ? s.images : s.imageUrl ? [s.imageUrl] : [],
        caption: s.caption,
        car: s.car as unknown as Spot['car'],
        location: s.location,
        city: s.city,
        countryCode: s.countryCode,
        event: s.event,
        taggedOwnerId: s.taggedOwnerId,
        taggedOwner: s.taggedOwner as unknown as Spot['taggedOwner'],
        likes: s.likes,
        beautiful: Array.isArray(s.beautiful) ? s.beautiful : [],
        cool: Array.isArray(s.cool) ? s.cool : [],
        sweetPhoto: Array.isArray(s.sweetPhoto) ? s.sweetPhoto : [],
        comments: s.comments as unknown as Spot['comments'],
        createdAt: s.createdAt,
        updatedAt: s.updatedAt,
        viewCount: s.viewCount ?? 0,
      }));
      setResults(spots);
      setHasSearched(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to search spots.');
      setResults([]);
      setHasSearched(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <h1>Search spots</h1>
        <p className={styles.subtitle}>Find spots by car make and model.</p>
        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.fields}>
            <label className={styles.field}>
              <span>Make</span>
              <select
                value={make}
                onChange={(e) => setMake(e.target.value)}
                disabled={carMakesLoading || !!carMakesError}
              >
                <option value="">{carMakesLoading ? 'Loading makes...' : 'All makes'}</option>
                {carMakes.map((carMake) => (
                  <option key={carMake.id} value={carMake.name}>{carMake.name}</option>
                ))}
              </select>
            </label>
            <label className={styles.field}>
              <span>Model</span>
              <input
                type="text"
                value={model}
                onChange={(e) => setModel(e.target.value)}
                placeholder="e.g. M3, Model S"
              />
            </label>
          </div>
          {carMakesError && <p className={styles.muted}>Could not load makes. You can still search by model.</p>}
          {error && <p className={styles.error}>{error}</p>}
          <button type="submit" className={styles.submit} disabled={loading}>
            {loading ? 'Searching...' : 'Search'}
          </button>
        </form>
      </div>

      {hasSearched && (
        <div className={styles.results}>
          {results.length === 0 ? (
            <p className={styles.muted}>No spots found. Try a different make or model.</p>
          ) : (
            results.map((spot) => <FeedCard key={spot.id} spot={spot} />)
          )}
        </div>
      )}
    </div>
  );
}
